import type { SearchRequestFilter, SearchRequestOrFilter } from './models';

import { isEdlpPricesEnabled } from '../../common/utils/edlpFeatureFlag';

export const LOOP_CAMPAIGN_PROMO_WIRE_TYPES = [
    'FIXED_PRICE',
    'MULTI_BUY_FIXED_PRICE',
    'PERCENTAGE_DISCOUNT',
] as const;

export const LOOP_AVAILABLE_PROMOTIONS_ATTRIBUTE = 'availablePromotions' as const;
export const LOOP_ONLINE_PROMOTIONS_ATTRIBUTE = 'onlinePromotions' as const;

/**
 * Loop54 indexes promo wire types on `availablePromotions` (flat string[]).
 * Present whenever `onlinePromotions` is; omitted on non-promo products.
 * Use equals for array membership — not `onlinePromotions` JSON contains.
 */
export const toAvailablePromotionTypeFilter = (promotionType: string): SearchRequestFilter => ({
    type: 'attribute',
    attributeName: LOOP_AVAILABLE_PROMOTIONS_ATTRIBUTE,
    value: promotionType,
    comparisonMode: 'equals',
});

/**
 * Loop54 indexes promotion type as JSON fragments; bare `FIXED_PRICE` also matches
 * `MULTI_BUY_FIXED_PRICE`. Typed contains (`type":"…"`) is the locked probe contract.
 */
export const toOnlinePromotionTypeContainsValue = (promotionType: string): string =>
    `type":"${promotionType}"`;

export const toOnlinePromotionTypeContainsFilter = (
    promotionType: string,
): SearchRequestFilter => ({
    type: 'attribute',
    attributeName: LOOP_ONLINE_PROMOTIONS_ATTRIBUTE,
    value: toOnlinePromotionTypeContainsValue(promotionType),
    comparisonMode: 'contains',
});

/**
 * Prefer `availablePromotions` equals when `EnableEdlpPrices` is on (field lives with EDLP).
 * Flag off → previous `onlinePromotions` typed contains so offers stay populated.
 */
export const toPromotionTypeFilter = (promotionType: string): SearchRequestFilter =>
    isEdlpPricesEnabled()
        ? toAvailablePromotionTypeFilter(promotionType)
        : toOnlinePromotionTypeContainsFilter(promotionType);

/**
 * Builds the filter out of known promotions types and filters out EDLP only promos.
 * EDLP + normal promo may still stay, then its priority field that we use.
 */
export const buildDefaultPromotionsFilter = (): SearchRequestOrFilter => ({
    or: LOOP_CAMPAIGN_PROMO_WIRE_TYPES.map(toPromotionTypeFilter),
});
